import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productData } from './productData.js';
import Picture from './components/Picture.jsx';

const VERTICAL_PRODUCT_IDS = new Set([
  'l1-leather-guard-ab',
  'i1-smart-fabric-ab',
  'c2-liquid-crystal',
  'g1-smart-glass',
  'g5-water-repellent',
]);

const CARD_HEIGHT_RATIO = 0.5;
const CARD_HEIGHT_MIN_RATIO = 0.5;
const SERVER_FALLBACK_HEIGHT = 600;
// Removed max height cap to allow cards to grow with content

const computeCardHeight = () => {
  if (typeof window === 'undefined') {
    return SERVER_FALLBACK_HEIGHT;
  }

  const viewportHeight = window.innerHeight;
  const dynamicHeight = viewportHeight * CARD_HEIGHT_RATIO;
  const minHeight = viewportHeight * CARD_HEIGHT_MIN_RATIO;
  // Use this as a base/min height only; allow growth beyond
  return Math.round(Math.max(minHeight, dynamicHeight));
};

// Ajuste fino de recorte superior da arte de cada card (valores aceitam %, px, etc.)
const IMAGE_CLIP_TOP_OVERRIDES = {
  'l1-leather-guard-ab': '0%',
  'i1-smart-fabric-ab': '0%',
  'g1-smart-glass': '0%',
  'g5-water-repellent': '0%',
  'c2-liquid-crystal': '0%',
};

// Ajuste de deslocamento vertical da arte (aceita valores CSS, ex.: '-8%', '12px')
const IMAGE_Y_OFFSET_OVERRIDES = {
  'l1-leather-guard-ab': '0%',
  'i1-smart-fabric-ab': '0%',
  'g1-smart-glass': '0%',
  'g5-water-repellent': '0%',
  'c2-liquid-crystal': '0%',  
};

// Permite ajustar a altura relativa da arte para produtos específicos
const IMAGE_HEIGHT_CLASS_OVERRIDES = {
  'l1-leather-guard-ab': 'h-[100%]',
  'i1-smart-fabric-ab': 'h-[100%]',
  'g5-water-repellent': 'h-[100%]',
  'c2-liquid-crystal': 'h-[100%]',

};

const ProductShowcase = ({ suppressBackgroundEffects = false }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [cardHeights, setCardHeights] = useState([]);
  const cardsRef = useRef([]);
  const imageSectionsRef = useRef([]);
  const backgroundRef = useRef(null);
  const contentRef = useRef(null);

  const updateCardHeights = useCallback(() => {
    const cards = cardsRef.current.filter(Boolean);
    if (!cards.length) {
      return;
    }

    const baseHeight = computeCardHeight();
    const rows = new Map();

    cards.forEach((card, index) => {
      const topKey = Math.round(card.offsetTop);
      const naturalHeight = Math.max(baseHeight, card.scrollHeight);
      if (!rows.has(topKey)) {
        rows.set(topKey, { indices: [], height: naturalHeight, maxImageHeight: 0 });
      }
      const row = rows.get(topKey);
      row.indices.push(index);
      row.height = Math.max(row.height, naturalHeight);
      // Track the tallest image section in this row
      const imgEl = imageSectionsRef.current[index];
      if (imgEl) {
        row.maxImageHeight = Math.max(row.maxImageHeight, imgEl.offsetHeight);
      }
    });

    const newHeights = cards.map(() => baseHeight);
    rows.forEach(({ indices, height, maxImageHeight }) => {
      indices.forEach((idx) => {
        newHeights[idx] = height;
        const imgEl = imageSectionsRef.current[idx];
        if (imgEl && maxImageHeight) {
          // Set a fixed height for the image section in the row so text starts aligned
          imgEl.style.minHeight = `${maxImageHeight}px`;
          imgEl.style.height = `${maxImageHeight}px`;
        }
      });
    });

    setCardHeights((prev) => {
      if (prev.length === newHeights.length && prev.every((value, idx) => value === newHeights[idx])) {
        return prev;
      }
      return newHeights;
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      updateCardHeights();
    };
    handleResize();
    const handleLoad = () => updateCardHeights();
    window.addEventListener('load', handleLoad);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', handleResize);
    };
  }, [updateCardHeights]);

  useLayoutEffect(() => {
    updateCardHeights();
  }, [updateCardHeights, isMobile, productData.length]);

  useEffect(() => {
    if (!isMobile && !suppressBackgroundEffects) {
      const handleScroll = () => {
        if (backgroundRef.current && contentRef.current) {
          const scrollPosition = window.pageYOffset;
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          const scrollPercentage = Math.min(scrollPosition / maxScroll, 1);

          const layer1Scale = 1 + scrollPercentage * 0.1;
          const layer2Scale = 1 + scrollPercentage * 1.0;
          const layer3Scale = 1 + scrollPercentage * 1.8;

          const maxScale = 2.5; // Limit maximum scale
          backgroundRef.current.style.setProperty('--scale-1', Math.min(layer1Scale, maxScale));
          backgroundRef.current.style.setProperty('--scale-2', Math.min(layer2Scale, maxScale));
          backgroundRef.current.style.setProperty('--scale-3', Math.min(layer3Scale, maxScale));

          // Adjust content position to compensate for background scaling
          const contentAdjustment = Math.max(0, (layer3Scale - 1) * 100);
          contentRef.current.style.marginTop = `${contentAdjustment}px`;
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      const handleScroll = () => {
        cardsRef.current.forEach((card) => {
          if (!card) return;
          const rect = card.getBoundingClientRect();
          const startPoint = window.innerHeight;
          const maxScalePoint = window.innerHeight / 2;
          const endPoint = 0;

          if (rect.top <= startPoint && rect.top >= endPoint) {
            let progress;
            if (rect.top > maxScalePoint) {
              progress = (startPoint - rect.top) / (startPoint - maxScalePoint);
            } else {
              progress = 1;
            }
            const scale = 1 + 0.1 * Math.min(progress, 1);
            card.querySelector('img').style.transform = `scale(${scale})`;
          } else {
            card.querySelector('img').style.transform = 'scale(1)';
          }
        });
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      cardsRef.current.forEach((card) => {
        if (card && card.querySelector('img')) {
          card.querySelector('img').style.transform = '';
        }
      });
    }
  }, [isMobile]);

  return (
    <div id="product-showcase" className="relative z-30 min-h-screen overflow-hidden">

      <div ref={contentRef} className={`relative z-10 ${isMobile ? 'bg-white' : ''}`}>
        <div className="container mx-auto py-16 px-4">
          <div className="mb-10 text-center">
            <h2 className="text-[26px] md:text-[28.5px] lg:text-[32.3px] xlg:text-[38px] font-bold tracking-[0.0em] text-gray-800">Linha Automotiva</h2>
          </div>
          
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-0 gap-y-10 ${isMobile ? 'gap-y-16' : ''}`}>
            {productData.map((product, index) => {
              const isVertical = VERTICAL_PRODUCT_IDS.has(product.id);

              // Remove fixed percentage height; height will be normalized per row via JS
              const imageSectionClass = `flex-none px-6 relative z-0 flex items-center justify-center overflow-hidden`;
              const baseHeightClass = isVertical ? 'h-[130%]' : 'h-[120%]';
              const imageHeightClass = IMAGE_HEIGHT_CLASS_OVERRIDES[product.id] || baseHeightClass;
              const imageClass = `w-auto object-contain transition duration-300 ease-in-out ${imageHeightClass} ${
                !isMobile ? 'group-hover:scale-110' : ''
              }`;
              const clipInsetTop = IMAGE_CLIP_TOP_OVERRIDES[product.id] || '0%';
              const imageTranslateY = IMAGE_Y_OFFSET_OVERRIDES[product.id] || (isVertical ? '-6%' : '-4%');

              return (
                <Link
                  key={product.id}
                  to={`/produtos/${product.id}`}
                  className="product-card bg-white dark:bg-white shadow-lg hover:shadow-xl group w-[90%] mx-auto flex flex-col overflow-hidden"
                  style={{
                    // Only enforce minHeight so content can expand naturally
                    minHeight: cardHeights[index] ? `${cardHeights[index]}px` : undefined,
                    backgroundColor: '#ffffff',
                  }}
                  ref={(el) => (cardsRef.current[index] = el)}
                >
                  {/* Rótulo */}
                  <div className="flex-none px-0 pb-[2%] bg-white dark:bg-white" style={{ backgroundColor: '#ffffff' }}>
                    <span
                      className="relative z-10 bg-[#FF0000] text-white font-bold uppercase tracking-[0.2em] text-[14px] pr-12 md:text-[14px] px-3 py-1.5 inline-block pr-6"
                      style={{ clipPath: 'polygon(0% 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)' }}
                    >
                      {(Array.isArray(product.categories) && product.categories.length
                        ? product.categories[product.categories.length - 1]
                        : product.category) || ''}
                    </span>
                  </div>

                  {/* Imagem */}
                  <div
                    className={`${imageSectionClass} bg-white dark:bg-white`}
                    style={{ clipPath: `inset(${clipInsetTop} 0 0 0)`, backgroundColor: '#ffffff' }}
                    ref={(el) => (imageSectionsRef.current[index] = el)}
                  >
                    <div
                      className="relative h-full w-full flex items-center justify-center"
                      style={{ transform: `translateY(${imageTranslateY})` }}
                    >
                      <Picture
                        src={`/images/${product.image}`}
                        alt={product.title}
                        className={imageClass}
                        onLoad={updateCardHeights}
                      />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex-1 px-6 pb-6 relative flex flex-col bg-white dark:bg-white" style={{ backgroundColor: '#ffffff' }}>
                    {/* So text starts at a stable Y; reduce overlay and remove negative margin */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-24" />
                    {/* Text block grows to push actions to bottom */}
                    <div className="relative mt-0 flex flex-col flex-1">
                      <h3 className="text-xl font-bold mb-4">{product.title}</h3>
                      <p className="text-gray-600 mb-4">{product.cardDescription || product.description}</p>
                    </div>
                    {/* Actions anchored at a fixed distance from bottom */}
                    <div className="pt-2">
                      <span className="inline-flex items-center justify-center px-4 py-2 bg-[#FF0000] text-white font-semibold uppercase tracking-[0.15em] text-xs md:text-sm transition duration-200 group-hover:bg-[#ff3b42]">
                        Saiba Mais
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;
